import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useEffect,
} from 'react';
import { TaskBundleDraft, TaskBundleMode } from '../../types';
import { reducer } from './reducer';
import { isEqual } from 'lodash';
import { TaskBundleContextValue } from './types';
import { getPageTitle } from '../../helpers';

interface TaskBundleProviderProps {
  mode: TaskBundleMode;
  initialDraft: TaskBundleDraft;
  children: React.ReactNode;
}

const TaskBundleContext = createContext<TaskBundleContextValue | undefined>(
  undefined
);

export const TaskBundleProvider: React.FC<TaskBundleProviderProps> = ({
  mode,
  children,
  initialDraft,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    initialDraft,
    draft: initialDraft,
    modifiedTemplateIds: new Set<string>(),
  });

  useEffect(() => {
    if (!isEqual(state.initialDraft, initialDraft)) {
      dispatch({ type: 'reset_draft', draft: initialDraft });
    }
  }, [initialDraft, state.initialDraft]);

  const value = useMemo<TaskBundleContextValue>(() => {
    const isOverviewValid = Boolean(
      state.draft.bundle.name?.trim().length &&
        state.draft.bundle.description?.trim().length
    );

    const isDirty = !isEqual(state.draft, state.initialDraft);
    const canSave = isOverviewValid && (mode !== 'edit' || isDirty);

    const pageTitle = getPageTitle(mode, state.draft.bundle.name);

    return {
      state,
      selectors: {
        draft: state.draft,
        mode,
        isDirty,
        isOverviewValid,
        canSave,
        pageTitle,
        templates: state.draft.templates,
        getBundleId: () => state.draft.bundle.id,
        getTemplateById: (id: string) =>
          state.draft.templates.find((template) => template.id === id),
        getTemplateDependencies: (templateId: string) => {
          const template = state.draft.templates.find(
            (template) => template.id === templateId
          );
          if (!template) return undefined;
          return template.dependencies;
        },
      },
      actions: {
        updateBundleField: (field, value) =>
          dispatch({ type: 'update_bundle_field', field, value }),
        addTemplate: (template) => dispatch({ type: 'add_template', template }),
        updateTemplate: (templateId, patch) =>
          dispatch({ type: 'update_template', templateId, patch }),
        removeTemplate: (templateId) =>
          dispatch({ type: 'remove_template', templateId }),
        reorderTemplates: (sourceIndex, destinationIndex) =>
          dispatch({
            type: 'reorder_templates',
            sourceIndex,
            destinationIndex,
          }),
        replaceTemplateDependencies: (templateId, dependencies) =>
          dispatch({
            type: 'replace_template_dependencies',
            templateId,
            dependencies,
          }),
        resetDraft: (draft) => dispatch({ type: 'reset_draft', draft }),
        markSaved: (draft) => dispatch({ type: 'mark_saved', draft }),
      },
    };
  }, [mode, state]);

  return (
    <TaskBundleContext.Provider value={value}>
      {children}
    </TaskBundleContext.Provider>
  );
};

export function useTaskBundleDraft() {
  const context = useContext(TaskBundleContext);

  if (!context) {
    throw new Error(
      'useTaskBundleDraft must be used within TaskBundleProvider'
    );
  }

  return context;
}
